import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import Placeholder from '@tiptap/extension-placeholder';
import { getEditorSelectedText, insertEditorLink } from './editorLinks';

// Types
interface MentionRecipient {
  id: number | string;
  name: string;
  email?: string;
  initials?: string;
}

interface UserColorMap {
  [userName: string]: string;
}

interface MentionPickData {
  id: number | string;
  name: string;
  email?: string;
}

interface RichEditorProps {
  mentionRecipients?: MentionRecipient[];
  userColorMap?: UserColorMap;
  onMentionPick?: (data: MentionPickData) => void;
  initialHTML?: string;
  placeholder?: string;
  onEmptyChange?: (isEmpty: boolean) => void;
  currentAudienceType?: number;
  setMentionedUsers?: React.Dispatch<React.SetStateAction<(string | number)[]>>;
  isTableView?: boolean;
  /** Optional async search — called when the user types after @.
   *  Should return a list of mentionable users/offices for the given query.
   *  When provided it replaces the local filter for non-empty queries. */
  onSearchMentions?: (query: string) => Promise<MentionRecipient[]>;
}

export interface RichEditorRef {
  isEmpty: () => boolean;
  getHTML: () => string;
  insertAtSymbol: () => void;
  setHTML?: (html: string) => void;
  focus: () => void;
  insertLink: ({ text, href }: { text?: string; href: string }) => void;
  getSelectedText: () => string;
}

interface MentionItem {
  id: string;
  name: string;
  email?: string;
  initials: string;
  color: string;
}

interface MentionProps {
  items: MentionItem[];
  command: (item: { id: string; label: string }) => void;
  clientRect?: () => DOMRect;
}

/**
 * Rich text editor component with mention support using TipTap
 *
 * @param mentionRecipients - Array of users that can be mentioned with @ symbol
 * @param userColorMap - Map of user names to colors for mention avatars
 * @param onMentionPick - Callback fired when a mention is selected
 * @param initialHTML - Initial HTML content for the editor
 * @param placeholder - Placeholder text for empty editor
 * @param onEmptyChange - Callback fired when editor empty state changes
 */
const RichEditor = forwardRef<RichEditorRef, RichEditorProps>(
  function TiptapEditor(
    {
      mentionRecipients,
      userColorMap,
      onMentionPick,
      initialHTML = '',
      placeholder = 'Write a comment...',
      onEmptyChange,
      currentAudienceType,
      setMentionedUsers,
      isTableView,
      onSearchMentions,
    },
    ref
  ) {
    const mutedMentions = useMemo(() => {
      if (!mentionRecipients) return [];
      // Example rule: only allow currentAudienceType 1 (my team) to be active
      if (currentAudienceType === 1) {
        return mentionRecipients
          .filter((u) => String(u.id) !== '1') // replace with logic comparing allowed users
          .map((u) => String(u.id));
      }
      return [];
    }, [mentionRecipients, currentAudienceType]);

    // Stable ref so the items closure always calls the latest onSearchMentions
    // without requiring the editor to be rebuilt on every render.
    const onSearchMentionsRef = useRef(onSearchMentions);
    useEffect(() => {
      onSearchMentionsRef.current = onSearchMentions;
    }, [onSearchMentions]);

    const editor = useEditor(
      {
        extensions: [
          StarterKit.configure({
            // @ts-expect-error - this isn't a valid option, but it was in the original JavaScript.
            // Maybe this is supposed to be `undoRedo: true`?
            history: true,
          }),
          Link.configure({
            openOnClick: false,
            autolink: false,
            linkOnPaste: false,
            protocols: ['http', 'https', 'mailto', 'tel'],
          }),

          Mention.configure({
            HTMLAttributes: { class: 'mention-node' },
            renderHTML({ node }) {
              const { id, label } = node.attrs;
              const isMuted = mutedMentions.includes(String(id));

              return [
                'span',
                {
                  class: `mention-node${isMuted ? '' : ''}`,
                  title: `@${label}`,
                  'data-tooltip': `@${label}`,
                  'user-id': `${id}`,
                },
                `@${label}`,
              ];
            },
            suggestion: {
              char: '@',
              items: ({ query }): MentionItem[] | Promise<MentionItem[]> => {
                const q = (query || '').toLowerCase();

                const toItem = (u: MentionRecipient): MentionItem => ({
                  id: String(u?.id),
                  name: u?.name,
                  email: u?.email,
                  initials:
                    u?.initials || (u?.name ? u?.name[0]?.toUpperCase() : '?'),
                  color: userColorMap?.[u?.name] || '#ccc',
                });

                // When the caller provides a live-search function and the user
                // has typed at least one character, delegate to it (this is how
                // CT gets offices via @WCNYC without flooding the list on open).
                if (q && onSearchMentionsRef.current) {
                  return onSearchMentionsRef
                    .current(q)
                    .then((results) => (results || []).map(toItem))
                    .catch((): MentionItem[] => []);
                }

                // Default: filter the static preloaded list client-side.
                return (mentionRecipients || [])
                  .filter(
                    (u) => !q || (u?.name || '').toLowerCase().includes(q)
                  )
                  .map(toItem);
              },
              render: () => {
                let popup: ReturnType<typeof tippy>;
                let container: HTMLElement;
                return {
                  onStart: (props) => {
                    container = document.createElement('div');
                    container.className = 'mention-dropdown';
                    renderMentionList(container, props, onMentionPick); // Pass suggestion props
                    popup = tippy('body', {
                      getReferenceClientRect: props.clientRect,
                      appendTo: () => document.body,
                      content: container,
                      showOnCreate: true,
                      interactive: true,
                      trigger: 'manual',
                      placement: 'bottom-start',
                    });
                  },
                  onUpdate: (props) => {
                    renderMentionList(container, props, onMentionPick);
                    popup?.[0]?.setProps({
                      getReferenceClientRect: props.clientRect,
                    });
                  },
                  onKeyDown: (props) => {
                    if (props.event.key === 'Escape') {
                      popup?.[0]?.hide();
                      return true;
                    }
                    return false;
                  },
                  onExit: () => {
                    popup?.[0]?.destroy();
                  },
                };
              },
            },
          }),
          Placeholder.configure({
            placeholder: placeholder,
          }),
        ],
        content: initialHTML || '',
      },
      [JSON.stringify(mentionRecipients)]
    );

    useImperativeHandle(ref, () => ({
      isEmpty: () => (editor ? editor.isEmpty : true),
      getHTML: () => (editor ? editor.getHTML() : ''),
      insertAtSymbol: () => editor?.chain().focus().insertContent('@').run(),
      focus: () => editor?.chain().focus().run(),
      insertLink: (link: { text?: string; href: string }) => {
        if (!editor) {
          return;
        }
        insertEditorLink(editor, link);
      },
      getSelectedText: () => {
        if (!editor) {
          return '';
        }
        return getEditorSelectedText(editor);
      },
    }));

    useEffect(() => {
      if (!editor) return;
      const onTransaction = () => {
        // Skipping heavy parsing to avoid re-renders.
      };
      editor.on('transaction', onTransaction);
      return () => {
        editor.off('transaction', onTransaction);
      };
    }, [editor]);

    // track empty state
    useEffect(() => {
      if (!editor) return;

      const handleUpdate = () => {
        const ids: (string | number)[] = [];

        editor.state.doc.descendants((node) => {
          if (node.type.name === 'mention') {
            const rawId = node.attrs.id;
            const parsedId =
              isNaN(Number(rawId)) || rawId === '' ? rawId : Number(rawId);
            ids.push(parsedId);
          }
        });

        setMentionedUsers?.(ids);
        const empty = !editor.getText().trim();
        if (empty) {
          setMentionedUsers([]);
        }
        onEmptyChange?.(empty);
      };
      editor.on('update', handleUpdate);
      return () => {
        editor.off('update', handleUpdate);
      };
    }, [editor, onEmptyChange, setMentionedUsers]);
    return (
      <div
        className={`custom-comment-box-tiptap ${isTableView ? 'responsive-custom-comment-box-tiptap' : ''}`}
      >
        <EditorContent editor={editor} />
      </div>
    );
  }
);
function renderMentionList(
  container: HTMLElement,
  props: MentionProps,
  onMentionPick?: (data: MentionPickData) => void
) {
  container.innerHTML = '';

  if (!props.items.length) {
    const noItem = document.createElement('div');
    noItem.textContent = 'No user found';
    noItem.style.padding = '6px 8px';
    noItem.style.fontSize = '12px';
    noItem.style.color = '#888';
    container.appendChild(noItem);
    return;
  }

  props.items.forEach((u) => {
    const option = document.createElement('div');
    option.className = 'mention-option';
    option.style.display = 'flex';
    option.style.alignItems = 'center';
    option.style.padding = '6px 8px';
    option.style.cursor = 'pointer';

    // Avatar
    const avatar = document.createElement('div');
    avatar.className = 'mention-avatar';
    avatar.style.width = '24px';
    avatar.style.height = '24px';
    avatar.style.borderRadius = '50%';
    avatar.style.marginRight = '8px';
    avatar.style.display = 'flex';
    avatar.style.alignItems = 'center';
    avatar.style.justifyContent = 'center';
    avatar.style.fontSize = '10px';
    avatar.style.color = '#fff';
    avatar.style.backgroundColor = u.color || '#ccc';
    avatar.textContent = u.initials || '?';

    // Text block
    const textWrap = document.createElement('div');
    textWrap.className = 'mention-text';

    const name = document.createElement('div');
    name.className = 'mention-name';
    name.textContent = u.name;

    const email = document.createElement('div');
    email.className = 'mention-email';
    email.textContent = u.email || '';
    email.style.fontSize = '12px';
    email.style.opacity = '0.8';

    textWrap.appendChild(name);
    textWrap.appendChild(email);

    option.appendChild(avatar);
    option.appendChild(textWrap);

    option.onclick = () => {
      // Insert the mention
      props.command({ id: u.id, label: u.name });

      // Notify parent
      onMentionPick?.({ id: u.id, name: u.name, email: u.email });
    };

    container.appendChild(option);
  });
}

export default RichEditor;
